/**
 * テナント管理ページ コンテナ
 */
import type React from "react";
import { useCallback, useContext } from "react";
import { AuthContext } from "../../../hooks/authProvider/authProvider";
import {
  useTenantMatchingSettings,
  useUpdateTenantMatchingSettings,
} from "../../../hooks/use-matching-settings-api";
import {
  type UpdateTenantInput,
  useTenantDetailApi,
  useUpdateTenantApi,
} from "../../../hooks/useTenantApi";
import { getCurrentTenant } from "../../../utils/authority-utils";
import { TenantPage } from "./TenantPage";

/** メール取り込み環境 */
const INTAKE_ENV = process.env.REACT_APP_ENV === "prd" ? "prd" : "dev";

const TenantPageContainer: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const tenant = getCurrentTenant(currentUser ?? null);
  const tenantId = tenant?.id ?? "";

  const { data, loading, error, refetch } = useTenantDetailApi(tenantId);
  const { updateTenant, loading: isUpdating } = useUpdateTenantApi();

  const {
    settings: tenantMatchingSettings,
    loading: tenantSettingsLoading,
    refetch: refetchTenantSettings,
  } = useTenantMatchingSettings();
  const { updateSettings: updateTenantSettings, loading: isTenantUpdating } =
    useUpdateTenantMatchingSettings();

  const intakeAddress = `${tenantId}@intake-${INTAKE_ENV}.strixsearch.com`;

  const handleUpdate = useCallback(
    async (input: UpdateTenantInput) => {
      await updateTenant(tenantId, input);
      await refetch();
    },
    [tenantId, updateTenant, refetch],
  );

  const handleAutoMatchingToggle = useCallback(
    async (enabled: boolean) => {
      await updateTenantSettings(enabled);
      await refetchTenantSettings();
    },
    [updateTenantSettings, refetchTenantSettings],
  );

  if (loading || tenantSettingsLoading) {
    return (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error.message}</span>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <TenantPage
      tenant={data}
      intakeAddress={intakeAddress}
      onUpdate={handleUpdate}
      isUpdating={isUpdating}
      autoMatchingEnabled={tenantMatchingSettings?.autoMatchingEnabled ?? false}
      onAutoMatchingToggle={handleAutoMatchingToggle}
      isAutoMatchingUpdating={isTenantUpdating}
    />
  );
};

export default TenantPageContainer;
